import React, {
  ComponentProps,
  MouseEvent,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Img, { ImgProps } from 'react-cool-img';
import { Popover } from '@mui/material';
import { debounce } from 'lodash';
import { colors } from 'spw-theme';
import {
  Row,
  Column,
  SPWDot,
  UnstyledReactLink,
} from 'spw-shared/globalComponents';
import { selectNotificationRequestTotals } from 'spw-redux/modules/join-requests/selectors';
import { getJoinRequestsForNotifications } from 'spw-redux/modules/join-requests/actions';
import { isTeamAdmin } from 'spw-shared/utils';
import { currentUser } from 'spw-redux/modules/user/selectors';
import { useHistory, useLocation } from 'react-router-v5';
import useCheckFloorplanOperationBeforeCalling from 'spw-shared/hooks/use-check-selection-mode-before-calling';

const NotificationsIcon = styled(({ src, ...props }: ImgProps) => (
  <Img {...props} src={src} />
))`
  && {
    cursor: pointer;
    margin-left: 24px;
  }
`;

const NotificationsMenuColumn = styled(Column)`
  && {
    min-width: 185px;
    width: 240px;
    background: #ffffff;
    border: 1px solid ${colors.borderBottomGray};
    box-shadow: 0px 6px 15px rgba(0, 0, 0, 0.11);
    border-radius: 5px;
  }
`;

const NotificationsMenuRow = styled(Row)<
  ComponentProps<typeof Row> & {
    allowHover: boolean;
  }
>`
  cursor: pointer;
  padding: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;

  &:hover {
    background-color: ${(props) =>
      props.allowHover ? colors.grayLight : 'inherit'};
  }
`;

const HeaderNotifications = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const history = useHistory();
  const pendingTransitionPath = useRef<string>(null);

  const user = useSelector(currentUser);
  const { projectRequestsTotal, teamRequestsTotal } = useSelector(
    selectNotificationRequestTotals
  );

  const src = useMemo(() => {
    if (projectRequestsTotal === null || teamRequestsTotal === null) return '';
    const unreadNotifications =
      projectRequestsTotal > 0 || teamRequestsTotal > 0;
    return unreadNotifications
      ? '/images/unread-notifications-icon.svg'
      : '/images/read-notifications-icon.svg';
  }, [projectRequestsTotal, teamRequestsTotal]);

  const menuItems = useMemo(() => {
    const items = [
      {
        label: 'Project Requests',
        length: projectRequestsTotal,
        path: '/projects/requests-to-join',
      },
    ];
    if (isTeamAdmin(user) || user.isOrgAdmin) {
      items.push({
        label: 'Team Requests',
        length: teamRequestsTotal,
        path: '/teams/requests-to-join',
      });
    }
    return items;
  }, [projectRequestsTotal, teamRequestsTotal, user]);

  const handleOpenNotificationsPopup = useCheckFloorplanOperationBeforeCalling(
    (event: SyntheticEvent) => {
      setAnchorEl(event.target);
    }
  );

  const handleClickAvatar = (event: SyntheticEvent) => {
    event.persist();

    handleOpenNotificationsPopup(event);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigateToPath = (
    e: MouseEvent<HTMLAnchorElement>,
    path: string
  ) => {
    e.preventDefault();
    pendingTransitionPath.current = path;
    setAnchorEl(null);
  };

  const debouncedGetJoinRequestsForNotifications = useMemo(
    () => debounce(() => dispatch(getJoinRequestsForNotifications()), 500),
    [dispatch]
  );

  useEffect(() => {
    if (!anchorEl && pendingTransitionPath.current) {
      history.push(pendingTransitionPath.current);
      pendingTransitionPath.current = null;
    }
  }, [anchorEl, history]);

  useEffect(() => {
    debouncedGetJoinRequestsForNotifications();
  }, [location, debouncedGetJoinRequestsForNotifications]);

  return (
    <>
      <NotificationsIcon
        data-testid="header-notifications-icon"
        src={src}
        onClick={handleClickAvatar}
        alt="notifications"
      />
      <Popover
        id="Notifications popover"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        disableScrollLock
      >
        <NotificationsMenuColumn>
          {menuItems.map(({ path, label, length }) => (
            <UnstyledReactLink
              to={path}
              key={label}
              onClick={(e) => handleNavigateToPath(e, path)}
            >
              <NotificationsMenuRow allowHover={length > 0}>
                <span>{label}</span>
                {length === 0 ? '0' : <SPWDot>{length}</SPWDot>}
              </NotificationsMenuRow>
            </UnstyledReactLink>
          ))}
        </NotificationsMenuColumn>
      </Popover>
    </>
  );
};

export default HeaderNotifications;
